import { useState } from 'react';
import { Avatar, Button, ConfirmDialog, MenuButton, Select, useToast } from '@dugzi/ui';
import { Building2, Cake, Copy, Home, Mail, MapPin, MoreHorizontal, NotebookPen, Pencil, Phone, Tag, Trash2, Users } from 'lucide-react';
import { api } from '../lib/api';
import { agoText, birthdayText, phoneText, savedText, useData, writableBooks } from '../lib/data';
import type { LabelCount, Person } from '../lib/types';

/** One card: who this is to you first (the context line), then how to reach them. */
export function PersonCard({ person: p, onEdit, onGone }: { person: Person; labels: LabelCount[]; onEdit(): void; onGone(): void }) {
  const { books, households, reload } = useData();
  const toast = useToast();
  const [asking, setAsking] = useState(false);
  const book = books.find((b) => b.id === p.book);
  const canEdit = book?.role !== 'view';
  const copyTargets = writableBooks(books).filter((b) => b.id !== p.book);
  const homes = households.filter((h) => h.book === p.book);

  // "Saved 12 March · How we met: Mia's swimming club · Changed by Ben 3 days ago"
  const context = [
    `Saved ${savedText(p.createdAt)}`,
    p.howWeMet && `How we met: ${p.howWeMet}`,
    book?.shared && p.updatedBy && `Changed by ${p.updatedBy} ${agoText(p.updatedAt)}`,
    p.linked && 'Follows the card it was copied from; changing the name or details here makes it this book\'s own',
  ].filter(Boolean).join(' · ');

  const del = async () => {
    try {
      await api(`/api/people/${p.id}`, { method: 'DELETE' });
      setAsking(false);
      toast.show(`${p.display} is in Trash for 30 days.`, { action: { label: 'Undo', onClick: () => { api(`/api/people/${p.id}/restore`, { body: {} }).then(() => reload()).catch(() => {}); } } });
      onGone();
    } catch (e) { toast.show((e as Error).message); }
  };
  const copy = async (bookId: string) => {
    const b = books.find((x) => x.id === bookId);
    try { await api(`/api/people/${p.id}/copy`, { body: { book: bookId } }); await reload(); toast.show(`Copied to ${b?.name}. How we met and notes stay private.`); } catch (e) { toast.show((e as Error).message); }
  };
  const setHome = async (id: string) => {
    try { await api(`/api/people/${p.id}/household`, { method: 'PUT', body: { household: id || null } }); await reload(); toast.show(id ? 'Added to the household.' : 'No longer in a household.'); } catch (e) { toast.show((e as Error).message); }
  };

  return (
    <article className="card" data-testid="person-card">
      <header className="card__head">
        <Avatar name={p.display} size={64} />
        <div className="card__title">
          <h2 data-testid="card-name">{p.display}</h2>
          {p.org && <p className="card__org">{p.org}</p>}
          <p className="card__context" data-testid="card-context">{context}</p>
        </div>
      </header>
      <div className="card__actions">
        {canEdit && <Button size="sm" icon={<Pencil aria-hidden />} onClick={onEdit} data-testid="edit-person">Edit</Button>}
        {canEdit && homes.length > 0 && (
          <Select label="Household" labelHidden size="sm" icon={<Home aria-hidden size={16} />} value={p.household?.id ?? ''} onValueChange={(v) => setHome(String(v))} testId="person-household"
            options={[{ value: '', label: 'No household' }, ...homes.map((h) => ({ value: h.id, label: h.name, hint: h.address ?? undefined }))]} />
        )}
        <MenuButton label={`More for ${p.display}`} icon={<MoreHorizontal aria-hidden />} variant="ghost" size="sm" testId="person-more" items={[
          ...copyTargets.map((b) => ({ label: `Copy to ${b.name}`, icon: <Copy aria-hidden />, hint: 'How we met and notes stay private', onSelect: () => copy(b.id) })),
          canEdit && { label: 'Delete', icon: <Trash2 aria-hidden />, danger: true, onSelect: () => setAsking(true), testId: 'delete-person' },
        ]} />
      </div>
      <dl className="card__facts">
        {p.emails.map((e) => <Fact key={e.value} icon={<Mail aria-hidden size={18} />} label={e.label || 'Email'}><a href={`mailto:${e.value}`}>{breakable(e.value)}</a></Fact>)}
        {p.phones.map((ph) => <Fact key={ph.value} icon={<Phone aria-hidden size={18} />} label={ph.label || 'Phone'}><a href={`tel:${ph.value}`}>{phoneText(ph.value)}</a></Fact>)}
        {p.household && <Fact icon={<Home aria-hidden size={18} />} label="Household">{p.household.name}{p.household.address && <><br /><span className="dz-muted">{p.household.address}</span></>}</Fact>}
        {p.addresses.map((a) => <Fact key={a.value} icon={<MapPin aria-hidden size={18} />} label={a.label || 'Address'}>{a.value}</Fact>)}
        {p.birthday && <Fact icon={<Cake aria-hidden size={18} />} label="Birthday">{birthdayText(p.birthday)}</Fact>}
        {p.org && <Fact icon={<Building2 aria-hidden size={18} />} label="Organisation">{p.org}</Fact>}
        {p.labels.length > 0 && <Fact icon={<Tag aria-hidden size={18} />} label="Labels"><span className="chips">{p.labels.map((l) => <span key={l} className="chip">{l}</span>)}</span></Fact>}
        {book?.shared && <Fact icon={<Users aria-hidden size={18} />} label="Address book">{book.name}</Fact>}
        {p.notes && <Fact icon={<NotebookPen aria-hidden size={18} />} label="Notes"><span className="card__notes">{p.notes}</span></Fact>}
      </dl>
      {!p.emails.length && !p.phones.length && !p.addresses.length && canEdit && (
        <p className="dz-muted dz-small">No way to reach {p.given || p.display} yet. <button type="button" className="linkbtn" onClick={onEdit}>Add an email or phone</button></p>
      )}
      <ConfirmDialog open={asking} onClose={() => setAsking(false)} onConfirm={del} title={`Delete ${p.display}?`} confirmLabel="Delete" icon={<Trash2 aria-hidden size={16} />}>
        <p className="dz-muted">{p.display} stays in Trash for 30 days, then is erased.{book?.shared ? ` They're removed from ${book.name} for everyone.` : ''}</p>
      </ConfirmDialog>
    </article>
  );
}

/** An address that wraps only after "@" and ".", never mid-word (P1-5). */
function breakable(v: string) {
  return v.split(/(?<=[@.])/).map((part, i) => <span key={i}>{i > 0 && <wbr />}{part}</span>);
}

function Fact({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="fact">
      <dt className="fact__label"><span className="fact__icon">{icon}</span>{label}</dt>
      <dd className="fact__value">{children}</dd>
    </div>
  );
}
